import { useEffect, useState, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { alertActions } from "../../store/alert";
import AlertDialog from "../utilities/AlertDialog";
import { ProjectModal } from "../admin/projects/ProjectModal";
import { addProject } from "../admin/projects/ProjectHelpers";
import { AuthContext } from "../context/auth-context";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

  // this modal is for editing the project and cost code information for an Order/ It also is the same modal used to add another container to the order
export const OrderModal = ({
  order,
  open,
  handleCancel,
  handleEditCommit,
  handleAddContainer,
  handleAddOrder,
  handlePurchaseContainer,
  modalType,
  modifyVendor
}) => {
  const [orderInfo, setOrderInfo] = useState({
    cost_code: "H4-40000",
    project: "",
    container: "",
    vendor_id: "ferg",
  });
  const [activeProjects, setActiveProjects] = useState([]);
  const [containers, setContainers] = useState([]);

  // Helper function to detect duplicate projects by both number AND name
  const detectDuplicateProjects = (projects) => {
    if (!projects || !Array.isArray(projects)) return [];
    
    const duplicates = [];
    const seenByBoth = new Map();
    
    projects.forEach((project, index) => {
      if (!project || !project.name || !project.number) return;
      
      // Create a combined key from both number and name
      const combinedKey = `${project.number}|${project.name}`;
      
      if (seenByBoth.has(combinedKey)) {
        duplicates.push({
          type: 'both',
          number: project.number,
          name: project.name,
          indices: [seenByBoth.get(combinedKey), index],
          projects: [projects[seenByBoth.get(combinedKey)], projects[index]]
        });
      } else {
        seenByBoth.set(combinedKey, index);
      }
    });
    
    return duplicates;
  };
  const [showModal, setShowModal] = useState(false);
  // this will open the add project dialog modal if we need to add a project
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [buttonText, setButtonText] = useState("Create Order");
  const [editVendor, setEditVendor] = useState(true)

  const auth = useContext(AuthContext);

  const dispatch = useDispatch();

  const requestConfig = useMemo(
    () => ({
      headers: {
        Authorization: "Bearer " + auth.token,
      },
    }),
    [auth.token]
  );

  // This first determines what the modal type is whether it is for adding a container or editing the order information
  // The useEffect gets the active projects only when the open modal is set to true. After getting the active projects from the database
  // it then selects the current project for the order and sets that in the modal it then shows the modal
  useEffect(() => {
    const getActiveProjects = async () => {
      try {
        if (modalType === "addContainer" || modalType === "purchaseContainer") {
          setButtonText(modalType === "addContainer" ? "Add Container" : "Purchase Container");
          const responseData = await axios.get(
            "/api/purchasing/containers",
            requestConfig
          );
          const serverContainers = responseData.data.containers;
          setContainers(serverContainers);
        } else {
          const responseData = await axios.get(
            "/api/projects/activeprojects",
            requestConfig
          );
          const serverActiveProjects = responseData.data.activeProjects;
          setActiveProjects(serverActiveProjects);

          // if it is an edit modal it sets the button name and then finds the current project in the list of active projects and sets it
          if (modalType === "edit") {
            setButtonText("Update Order");
            // I have to find the current project index in the list of active projects to set the modal
            const currentProjectIndex = serverActiveProjects.findIndex(
              (project) => {
                return project.id === order.project.id;
              }
            );

            if (currentProjectIndex < 0) {
              dispatch(
                alertActions.showAlert({
                  title: "Not An Active Project",
                  message:
                    "Your current project is not an active project in the system.  Please set the project to active or synchronize the projects from Stratus",
                })
              );
              return;
            }

            setOrderInfo({
              cost_code: order.cost_code,
              project: serverActiveProjects[currentProjectIndex],
              vendor_id:order.vendor_id
            });

            setEditVendor(modifyVendor);
          } else if (modalType === "createReturn") {
            setButtonText("Create Return");
          } else {
            setButtonText("Create Order");
          }
        }

        setShowModal(true);
        
        // Check for duplicates when modal opens (only for project modals)
        if (modalType !== "addContainer" && modalType !== "purchaseContainer") {
          const duplicates = detectDuplicateProjects(activeProjects);
          if (duplicates.length > 0) {
            const duplicateMessages = duplicates.map(dup => {
              return `Duplicate project "${dup.number} - ${dup.name}" found in projects: ${dup.projects.map(p => `${p.number} - ${p.name}`).join(', ')}`;
            });
            
            dispatch(
              alertActions.showAlert({
                title: "Duplicate Projects Detected",
                message: `Warning: ${duplicateMessages.join('. ')}. Please review and remove duplicates from the system.`,
                severity: "warning"
              })
            );
          }
        }
      } catch (err) {
        console.log("Error receiving active projects: ", err);
        dispatch(
          alertActions.showAlert({
            title: "Error Receiving Active Projects",
            message:
              "There was an error receiving the active projects: " +
              err.response.data.message,
          })
        );
      }
    };
    if (open) {
      getActiveProjects();
    }
  }, [open, requestConfig, order, modalType]);

  // this updates the state when the value is changed in the modal
  const handleChange = (e) => {
    let itemChanged;

    // Always use the field name from the event target, except for container selection
    if ((modalType === "addContainer" || modalType === "purchaseContainer") && e.target.name === "project") {
      itemChanged = "container";
    } else {
      itemChanged = e.target.name;
    }

    const valueChanged = e.target.value;
    const changeOrderInfo = { ...orderInfo, [itemChanged]: valueChanged };

    setOrderInfo(changeOrderInfo);
    console.log(orderInfo);
  };

  // this handles changes for the Autocomplete component
  const handleAutocompleteChange = (event, newValue) => {
    let itemChanged;

    if (modalType === "addContainer" || modalType === "purchaseContainer") {
      itemChanged = "container";
    } else {
      itemChanged = "project";
    }

    const changeOrderInfo = { ...orderInfo, [itemChanged]: newValue };
    setOrderInfo(changeOrderInfo);
    console.log(orderInfo);
  };

  // this handles input changes in the Autocomplete to check for duplicates
  const handleAutocompleteInputChange = (event, value, reason) => {
    // Only check for duplicates when user is typing (not when selecting)
    if (reason === 'input' && modalType !== "addContainer" && modalType !== "purchaseContainer") {
      const duplicates = detectDuplicateProjects(activeProjects);
      
      if (duplicates.length > 0) {
        const duplicateMessages = duplicates.map(dup => {
          return `Duplicate project "${dup.number} - ${dup.name}" found in projects: ${dup.projects.map(p => `${p.number} - ${p.name}`).join(', ')}`;
        });
        
        dispatch(
          alertActions.showAlert({
            title: "Duplicate Projects Detected",
            message: `Warning: ${duplicateMessages.join('. ')}. Please review and remove duplicates from the system.`,
            severity: "warning"
          })
        );
      }
    }
  };

  // this is if the action is canceled and the modal is closed
  const handleClose = () => {
    setShowModal(false);
    setOrderInfo({
      cost_code: "H4-40000",
      project: "",
      container: "",
      vendor_id: "ferg",
    });
    handleCancel();
  };

  // this handles when the changes are submitted
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowModal(false);

    if (modalType === "edit") {
      handleEditCommit(orderInfo);
    } else if (modalType === "addContainer") {
      handleAddContainer(orderInfo.container.id);
    } else if (modalType === "purchaseContainer") {
      handlePurchaseContainer(orderInfo);
    } else {
      handleAddOrder(orderInfo);
    }
    setOrderInfo({
      cost_code: "H4-40000",
      project: "",
      container: "",
      vendor_id: "ferg",
    });
  };

  // this is if the user needs to add a project
  const handleAddProject = () => {
    setShowModal(false);
    setAddProjectOpen(true);
  };

  // this handles when a new project is added through the add project modal
  const submitNewProject = async (project) => {
    const id = uuidv4();
    const projectToAdd = { ...project, id: id };
    console.log("Here is the project to add: ", projectToAdd);

    const activeProjectsBefore = activeProjects;
    const orderInfoBefore = orderInfo;

    setActiveProjects([projectToAdd, ...activeProjects]);
    setOrderInfo({ ...orderInfo, project: projectToAdd });
    setShowModal(true);
    setAddProjectOpen(false);

    try {
      const addedProject = await addProject(projectToAdd, requestConfig);
      console.log("Here is the project that was added: ", addedProject);
    } catch (error) {
      console.log("There was an error adding the project: ", error);
      setActiveProjects(activeProjectsBefore);
      setOrderInfo(orderInfoBefore);
    }
  };

  // this is if the add project is canceled
  const handleCancelAddProject = async (project) => {
    setAddProjectOpen(false);
    setShowModal(true);
  };

  return (
    <div>
      <AlertDialog />
      <Dialog open={showModal}>
        <DialogTitle>
          {modalType === "addContainer"
            ? "Select Container to Add"
            : modalType === "purchaseContainer"
            ? "Purchase Container"
            : "Order Information"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Autocomplete
              id="project"
              options={
                modalType !== "addContainer" && modalType !== "purchaseContainer"
                  ? activeProjects || []
                  : containers || []
              }
              value={
                modalType === "addContainer" || modalType === "purchaseContainer"
                  ? orderInfo.container || null
                  : orderInfo.project || null
              }
              onChange={handleAutocompleteChange}
              onInputChange={handleAutocompleteInputChange}
              getOptionLabel={(option) => {
                if (!option) return "";
                if (modalType !== "addContainer" && modalType !== "purchaseContainer") {
                  return `${option.number || ""} - ${option.name || ""}`;
                } else {
                  return option.name || "";
                }
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.id === value.id;
              }}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                
                const searchText = inputValue.toLowerCase().trim();
                if (!searchText) return options;
                
                return options.filter((option) => {
                  if (!option) return false;
                  
                  if (modalType !== "addContainer" && modalType !== "purchaseContainer") {
                    // For projects, search in both number and name
                    const numberMatch = option.number?.toLowerCase().includes(searchText) || false;
                    const nameMatch = option.name?.toLowerCase().includes(searchText) || false;
                    return numberMatch || nameMatch;
                  } else {
                    // For containers, search in name
                    return option.name?.toLowerCase().includes(searchText) || false;
                  }
                });
              }}
              noOptionsText="No options found"
              clearOnEscape
              selectOnFocus
              handleHomeEndKeys
              freeSolo={false}
              disableClearable={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={modalType === "addContainer" || modalType === "purchaseContainer" ? "Container" : "Project"}
                  required
                  sx={{ mt: 1 }}
                />
              )}
              fullWidth
              sx={{ mt: 1 }}
            />
            <TextField
              required
              fullWidth
              margin="normal"
              id="cost_code"
              label="Cost Code"
              name="cost_code"
              autoComplete="off"
              type="text"
              value={orderInfo.cost_code}
              onChange={handleChange}
              sx={{
                visibility: modalType === "addContainer" ? "hidden" : "visible",
              }}
            />
            <FormControl sx={{ mt: 1, minWidth: 120, visibility: modalType === "addContainer" || modalType === "purchaseContainer" ? "hidden" : "visible" }} fullWidth>
              <InputLabel id="vendor-label">Vendor</InputLabel>
              <Select
                labelId="vendor-label"
                id="vendor"
                value={orderInfo.vendor_id}
                name="vendor_id"
                label="Vendor"
                onChange={handleChange}
                required
                disabled={!editVendor}
              >
                <MenuItem key="ferg" value="ferg">
                  Ferguson
                </MenuItem>
                <MenuItem key="mks" value="mks">
                  MKS
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" sx={{ width: 200, whiteSpace: "nowrap" }}>
              {buttonText}
            </Button>
            <Button
              variant="outlined"
              sx={{
                width: 150,
                visibility: modalType === "edit" ? "visible" : "hidden",
              }}
              onClick={handleAddProject}
            >
              Add Project
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ width: "25%" }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ProjectModal
        editProject={false}
        open={addProjectOpen}
        handleCancel={handleCancelAddProject}
        handleAddProject={submitNewProject}
      />
    </div>
  );
};
